import React, { useState, useEffect, useRef, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { supportedLanguages, SupportedLanguage } from '../lib/i18n';
import { LanguageContext, type LanguageContextType } from '../hooks/useLanguage';

interface LanguageProviderProps {
  children: ReactNode;
}

export function LanguageProvider({ children }: LanguageProviderProps) {
  const { i18n } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const [isChanging, setIsChanging] = useState(false);
  const [languageKey, setLanguageKey] = useState(0); // Increment to force re-renders
  const [currentLanguage, setCurrentLanguage] = useState<SupportedLanguage>(
    (i18n.language?.substring(0, 2) as SupportedLanguage) || 'en'
  );
  
  // Track the last language we explicitly set (either from user pref sync or manual change)
  const lastSetLanguageRef = useRef<string | null>(null);
  // Track if we're in the middle of a manual language change
  const isManualChangeRef = useRef(false);

  // Sync language when authenticated - DB is the source of truth
  // If user has no preferred_language in DB, detect from browser and SAVE to DB
  useEffect(() => {
    // Skip if we're in the middle of a manual change
    if (isManualChangeRef.current) {
      // console.log('🌐 [LanguageProvider] Skipping sync - manual change in progress');
      return;
    }
    
    if (!isAuthenticated || !user) {
      return;
    }

    const syncLanguage = async () => {
      // Case 1: User HAS a preferred_language in DB - use it (DB is source of truth)
      if (user.preferred_language) {
        const userLang = user.preferred_language as SupportedLanguage;
        
        // Only sync if this is a NEW preference from the backend
        // (different from what we last set)
        if (userLang !== lastSetLanguageRef.current && supportedLanguages.some(l => l.code === userLang)) {
          // console.log('🌐 [LanguageProvider] Syncing to DB preference:', userLang, '(last set was:', lastSetLanguageRef.current, ')');
          lastSetLanguageRef.current = userLang;
          await i18n.changeLanguage(userLang);
          setCurrentLanguage(userLang);
          setLanguageKey(k => k + 1); // Force re-render
          localStorage.setItem('preferred_language', userLang);
        }
      } 
      // Case 2: User has NO preferred_language in DB - detect and SAVE to DB
      else {
        let finalLang: SupportedLanguage = 'en'; // Default fallback
        
        // Step 1: Try to get language from browser (i18next detection)
        const browserLang = (i18n.language?.substring(0, 2) as SupportedLanguage);
        const isBrowserLangValid = browserLang && supportedLanguages.some(l => l.code === browserLang);
        
        if (isBrowserLangValid) {
          // Browser gave us a valid supported language
          finalLang = browserLang;
          // console.log('🌐 [LanguageProvider] Using browser language:', finalLang);
        } else {
          // Step 2: Browser detection failed - try IP-based detection from backend
          // console.log('🌐 [LanguageProvider] Browser language not supported, trying IP detection...');
          try {
            const response = await api.get<{ detected_language: string | null; fallback_language: string }>('/user/detect-language');
            const ipLang = response.data.detected_language as SupportedLanguage | null;
            
            if (ipLang && supportedLanguages.some(l => l.code === ipLang)) {
              finalLang = ipLang;
              // console.log('🌐 [LanguageProvider] Using IP-detected language:', finalLang);
            } else {
              // Step 3: IP detection also failed - use fallback
              finalLang = (response.data.fallback_language as SupportedLanguage) || 'en';
              // console.log('🌐 [LanguageProvider] IP detection failed, using fallback:', finalLang);
            }
          } catch (error) {
            // console.error('🌐 [LanguageProvider] IP detection request failed:', error);
            // Use English as ultimate fallback
            finalLang = 'en';
            // console.log('🌐 [LanguageProvider] Using ultimate fallback: en');
          }
        }
        
        // console.log('🌐 [LanguageProvider] Final detected language:', finalLang, '- saving to DB');
        
        // Save the detected language to DB so it becomes the source of truth
        try {
          await api.put('/user/profile', { preferred_language: finalLang });
          // console.log('🌐 [LanguageProvider] Saved detected language to DB:', finalLang);
          
          // Update local state
          lastSetLanguageRef.current = finalLang;
          await i18n.changeLanguage(finalLang);
          setCurrentLanguage(finalLang);
          setLanguageKey(k => k + 1);
          localStorage.setItem('preferred_language', finalLang);
        } catch (error) {
          // console.error('🌐 [LanguageProvider] Failed to save detected language to DB:', error);
          // Still apply the detected language locally even if save fails
          lastSetLanguageRef.current = finalLang;
          await i18n.changeLanguage(finalLang);
          setCurrentLanguage(finalLang);
          setLanguageKey(k => k + 1);
          localStorage.setItem('preferred_language', finalLang);
        }
      }
    };

    syncLanguage();
  }, [isAuthenticated, user, i18n]);

  // Reset tracking when user logs out
  useEffect(() => {
    if (!isAuthenticated) {
      lastSetLanguageRef.current = null;
    }
  }, [isAuthenticated]);

  // Sync with i18n language changes (from external sources)
  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      // console.log('🌐 [LanguageProvider] i18n languageChanged event:', lng);
      const langCode = lng.substring(0, 2) as SupportedLanguage;
      if (supportedLanguages.some(l => l.code === langCode)) {
        setCurrentLanguage(langCode);
        setLanguageKey(k => k + 1); // Force re-render
      }
    };

    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  const changeLanguage = async (lang: SupportedLanguage) => {
    // console.log('🌐 [LanguageProvider] changeLanguage called:', lang, 'current:', currentLanguage);
    
    if (lang === currentLanguage) {
      // console.log('🌐 [LanguageProvider] Same language, skipping');
      return;
    }
    
    // Mark that we're doing a manual change - prevents sync effect from interfering
    isManualChangeRef.current = true;
    // Update the ref FIRST to prevent sync effect from reverting
    lastSetLanguageRef.current = lang;
    
    setIsChanging(true);
    try {
      // Change i18n language - this triggers the languageChanged event
      // console.log('🌐 [LanguageProvider] Calling i18n.changeLanguage:', lang);
      await i18n.changeLanguage(lang);
      
      // Update state
      setCurrentLanguage(lang);
      setLanguageKey(k => k + 1); // Force re-render
      localStorage.setItem('preferred_language', lang);
      // console.log('🌐 [LanguageProvider] Language changed to:', lang, 'i18n.language:', i18n.language);

      // If authenticated, save to backend
      if (isAuthenticated) {
        try {
          await api.put('/user/profile', { preferred_language: lang });
          // console.log('🌐 [LanguageProvider] Backend save successful for:', lang);
        } catch (error) {
          // console.error('🌐 [LanguageProvider] Failed to save to backend:', error);
        }
      }
    } catch (error) {
      // console.error('🌐 [LanguageProvider] Error changing language:', error);
    } finally {
      setIsChanging(false);
      // Reset the manual change flag after a short delay
      // This gives React time to process all updates
      setTimeout(() => {
        isManualChangeRef.current = false;
      }, 500);
    }
  };

  const value: LanguageContextType = {
    currentLanguage,
    changeLanguage,
    supportedLanguages,
    isChanging,
    languageKey,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}
